import { ColorClubMatas5, Spacing12, Spacing16, Spacing24, Spacing40, Spacing8 } from 'autogen/design-tokens/tokens';
import { type CountDownBannerModel, HideCountdownBanner } from 'autogen/swagger/all';
import {
    CloseButtonAriaLabel,
    LinkText,
    Overline,
    PreLinkText
} from 'autogen/translation-keys/trans-website-booking-onetoonebanner-beforebooking';
import { ButtonWithIcon } from 'components/Button';
import InlinePageActionButton from 'components/generic/InlineButton/InlinePageActionButton';
import { Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import ContainerFluid from 'components/Layout/components/ContainerFluid';
import Flex from 'components/Layout/components/Flex';
import SimpleCountdown from 'components/liveEvents/simpleCountdown';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { breakpoint, sizes } from 'variables';

// #region Styles
const Wrapper = styled.div`
    background-color: ${ColorClubMatas5};
`;

const FlexContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${Spacing12};
    padding-top: ${Spacing16};
    padding-bottom: ${Spacing16};

    ${breakpoint.up(sizes.md)} {
        flex-direction: row;
        justify-content: space-between;
        padding-top: ${Spacing40};
        padding-bottom: ${Spacing40};
        gap: ${Spacing24};
    }
`;

const StyledSimpleCountdown = styled(SimpleCountdown)`
    align-self: center;
`;

const StyledLabel = styled(Typography.Label)`
    margin-bottom: 0;
    text-align: center;

    ${breakpoint.up(sizes.md)} {
        text-align: left;
    }
`;

const StyledTitle = styled(Typography.Title)`
    margin-bottom: 0;
    margin-top: 0;
    text-align: center;

    ${breakpoint.up(sizes.md)} {
        text-align: left;
    }
`;

const StyledBody = styled(Typography.Body)`
    margin-bottom: 0;
    text-align: center;

    ${breakpoint.up(sizes.md)} {
        text-align: left;
    }
`;

const StyledButtonWithIcon = styled(ButtonWithIcon)`
    position: absolute;
    top: 0;
    right: -${Spacing16};
    height: auto;

    ${breakpoint.up(sizes.md)} {
        position: static;
        align-self: flex-start;
        margin-top: -${Spacing8};
    }
`;

// #endregion Styles

export default function OneToOneBanner({ model }: { model: CountDownBannerModel }) {
    const { name, startTime, pageAction } = model;
    const startTimeDate = new Date(startTime);
    const { t } = useTranslation();
    const [hideBanner, setHideBanner] = useState<boolean>(false);

    const closeBanner = (): void => {
        setHideBanner(true);
        HideCountdownBanner();
    };

    return !hideBanner ? (
        <Wrapper>
            <ContainerFluid>
                <FlexContainer>
                    <StyledSimpleCountdown startTime={startTimeDate} />

                    <Flex.Column gap={Spacing8}>
                        <StyledLabel size="S">{t(Overline)}</StyledLabel>

                        <StyledTitle size="L" forwardedAs="h2">
                            {name}
                        </StyledTitle>

                        {pageAction?.action && (
                            <StyledBody size="M">
                                {t(PreLinkText)}
                                {' - '}
                                <InlinePageActionButton action={pageAction}>{t(LinkText)}</InlinePageActionButton>
                            </StyledBody>
                        )}
                    </Flex.Column>

                    <StyledButtonWithIcon
                        aria-label={t(CloseButtonAriaLabel)}
                        onClick={closeBanner}
                        variant="ghostOnLight"
                        icon={<Icon type="Close" size={16} />}
                        size="sm"
                    />
                </FlexContainer>
            </ContainerFluid>
        </Wrapper>
    ) : null;
}
